import {Login} from 'components/auth/Login';
import {Loading} from 'components/loading/Loading';
import {isATypeOfStudent, isInCourse} from 'contexts/profileUtil';
import {ProfileContext} from 'contexts/ProfileContext';
import {CourseThemeProvider} from 'course/contexts/ThemeContext';
import {HomeRedirect} from 'course/splash/HomeRedirect';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useContext} from 'react';
import {Navigate} from 'react-router-dom';

// Hardcoded rather than read from the `:courseId` route param: this page is
// also what renders for garbage/unknown course ids (e.g. `/asdfasdl`), and in
// those cases the URL's courseId isn't a real, applyable-to course. PAI only
// has one active cohort right now, so pointing every visitor at it is the
// reasonable default until this page is made cohort-aware.
const PAI_COURSE_ID = 'pai1';

/**
 * PAI's course-home page, shown at `/<courseId>` in place of CIP's default
 * role-based redirect (`HomeRedirect`).
 *
 * Signed-in users with a role in the course are sent on to their
 * role-specific course home exactly as CIP would do, except students who
 * have not confirmed their place in onboarding. They and other signed-in
 * users go to the student application page, which holds their application
 * status and, once results are out, their admission letter. Logged-out
 * visitors get the sign-in form in place, so signing in lands them back here
 * and on through the same routing.
 *
 * The route renders outside `Authenticated`. The profile comes from the
 * `ProfileProvider` that CIP's `CourseScopeLayout` mounts above the whole
 * course tree; it follows auth state, so `loading` covers both the auth
 * restore on a hard reload and the user documents, and a signed-out visitor
 * resolves to no `userData`.
 */
export function PaiCourseHomePage() {
  const {userData, loading} = useContext(ProfileContext);
  const courseId = useCourseId();
  if (loading) {
    return <Loading />;
  }
  if (!userData) {
    return (
      <CourseThemeProvider courseId={courseId}>
        <Login />
      </CourseThemeProvider>
    );
  }
  const isUnconfirmedStudent =
    isATypeOfStudent(userData.courseRole) &&
    !userData.confirmedStudent?.[courseId];
  if (isInCourse(userData.courseRole) && !isUnconfirmedStudent) {
    return <HomeRedirect />;
  }
  return <Navigate to={`/apply/${PAI_COURSE_ID}/student`} replace />;
}
